import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend } from 'chart.js';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {apiUrl, apiPageLimit, reportEndDateDiff, reportStartDateDiff} from '../../../../util/Api';
import { toFactDate, fromFactDate, isFactDate, getColors, getColor, getFactDateRange, toDisplayDate, roundNum } from '../../../../util/ChartUtils';
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, ArcElement, Title, Tooltip, Legend);

const GuardrailSummaryCharts = (props) => {
    //console.log('in GuardrailSummaryCharts')
    //let data = props.data;
    //let aggData = props.data;

    const [metrics, setMetrics] = useState([]);
    const [range, setRange] = useState(null);

    const [searchParams] = useSearchParams();
    let alertId = searchParams.get('alertId');
    let typeId = searchParams.get('typeId');

    let page = searchParams.get('page') ? searchParams.get('page') : 1;
    let pageLimit = searchParams.get('limit') ? searchParams.get('limit') : apiPageLimit;
   
    let { accountKey, appKey, startFactDate, endFactDate } = useParams();

    let navigate = useNavigate();

    useEffect(() => {
        let startDate = new Date();
        let endDate = new Date();
        let startFact = startFactDate;
        let endFact = endFactDate;

        if (!isFactDate(endFact)) {
            //console.log('endFactDate is null')
            endDate.setDate(endDate.getDate() - reportEndDateDiff);
            endFact = toFactDate(endDate);
        } else {
            //console.log('endFactDate is not null', endFact)
            endDate = fromFactDate(endFact);
        }

        if (!isFactDate(startFact)) {
            //console.log('startFactDate is null')
            startDate.setDate(endDate.getDate() - reportStartDateDiff);
            startFact = toFactDate(startDate);
        } else {
            //console.log('startFactDate is not null', startFact)
            startDate = fromFactDate(startFact);
        }

        setRange({ startDate: startFact, endDate: endFact });

        //document.title = "reconify guardrails";

        //setStartDatePicker(startDate);
        //setEndDatePicker(endDate);

        let options = {
            accountKey: accountKey,
            appKey: appKey,
            startDate: startFact,
            endDate: endFact,
            isActive:1,
            //limit: pageLimit,
            //limitStart: (pageLimit * (page-1))
        }
        if(alertId != null && alertId != ""){
            options.alertId = alertId;
        }
        if(typeId != null && typeId != ""){
            options.typeId = typeId;
        }
        let isAuthError = false;

        axios.post(apiUrl + '/guardrails/logsummary',
            options, { withCredentials: true })
            .then((res) => {
                if (res.data.status === 'ok') {
                    //clear errors 
                    if (res.data.logs !== null) {
                        setMetrics(res.data.logs);
                    }
                    //if (res.data.recordCount !== null) {
                    //    setRecordCount(res.data.recordCount);
                    //}
                } else {
                    if(metrics?.length > 0) {
                        setMetrics([])
                    }

                    let redirectToLogin = false;
                    res.data.errors.forEach((error, i) => {
                        switch (error) {
                            case 'auth-expired':
                            case 'auth-error':
                            case 'no-auth':
                            case 'no-valid-account':
                            case 'no-permissions':
                                //console.log('auth expired');
                                isAuthError = true;
                                redirectToLogin = true;
                                break;
                            default:
                                //do nothing
                        }
                    });
                    //don't call setErrors if not logged in as it throws an unmounted error
                    if (redirectToLogin) {
                        navigate('/login');
                        return;
                    }
                }
            })
            .catch((err) => {

            });
    }, [accountKey, appKey, navigate, page, pageLimit, startFactDate, endFactDate, alertId])


    const TagLineChart = (props) => {
        //let range = props.range;
        let data = props.data;
        if (data == null || data.length < 1) {
            return null;
        }

        let dateRange = getFactDateRange(range.startDate, range.endDate)
        //console.log('range',dateRange);

        /*
        if (dateRange.length > data.length) {
            //have missing periods that need to be fixed
            for (let i = 0; i < dateRange.length; i++) {
                //console.log(dateRange[i].factDate)
                //needs to be != and not !== as that compares type and value
                //the number is sometimes technically a string
                if (i >= data.length || dateRange[i].factDate != data[i].factDate) {
                    //console.log('missing', dateRange[i].factDate)
                    data.splice(i, 0, { factDate: dateRange[i].factDate })
                }
            }
        }
        */

        //console.log(dateRange)
        let dates = [];
        let interactions = [];

        //build data
        let alerts = []
        for (const elem of data){
            if(alerts[elem.alertId] === undefined){
                alerts[elem.alertId] = {
                    tag: elem.tag,
                    data: []
                }
            }
            alerts[elem.alertId].data.push({factDate: elem.factDate, interactions:elem.interactions}) 
        }
        //console.log(alerts);
        alerts = alerts.filter(el => el);
        //console.log(alerts);

        //fix missing dates if any
        for(const alert of alerts) {
            if(alert.data.length < dateRange.length){
                for(let i=0; i< dateRange.length; i++){
                    if(i >= alert.data.length || dateRange[i].factDate != alert.data[i].factDate){
                        alert.data.splice(i, 0, {factDate: dateRange[i].factDate, interactions:null})
                    }
                }
            }
        }

        let labels = dateRange.map((elem, e) => (
            toDisplayDate(elem.factDate)
        ))

        let datasets = alerts.map((elem, e)=>{
            return {
                label: elem.tag, 
                data: elem.data.map(d => d.interactions),
                backgroundColor: getColor(e),
                borderColor: getColor(e),
                yAxisID: 'y'
            }
        })

        let colors = getColors(data.length);
        //let total = metrics.reduce((temp, x) => temp + x, 0);
        return (
            <div className='chart-card'>
                <div className='chart-title'>Guardrails</div>
                <Line data={
                    {
                        labels: labels,
                        datasets: datasets
                    }
                }
                options={{ legend: { display: true, position: 'top' }, 
                responsive: true,
                scales: { 
                    y: { 
                        suggestedMin: 0 ,
                        border:{dash: [3, 3], color:'#dedede'},
                        grid: {
                            color: '#e3e3e3',
                        }
                        
                    }, 
                    x: {
                        border:{dash: [3, 3], color:'#dedede'},
                        grid: {
                            color: '#e3e3e3',
                        }
                    }
                },
                plugins : {legend : {labels : {usePointStyle : true, boxHeight: 8}, align:'end'}} }} />
            </div>
        )
    }

    const TagPieChart = (props) => {
        let data = props.data;
        //console.log('data', data)
        if (data == null || data.length < 1) {
            return null;
        }

        let alerts = [];
        //get all the tags as labels
        for (const elem of data){
            if(alerts[elem.alertId] === undefined){
                alerts[elem.alertId] = {
                    tag: elem.tag,
                    interactions: elem.interactions
                }
            } else {
                alerts[elem.alertId].interactions += elem.interactions
            }
        }
        //console.log(alerts)
        alerts = alerts.filter((el)=>{
            return el !=null
        })
        //console.log(alerts)

        let labels = alerts.map((elem, e) => (
            elem.tag
        ))
        let metrics = alerts.map((elem, e) => (
            elem.interactions
        ))
        let colors = getColors(data.length);
        let total = metrics.reduce((temp, x) => temp + x, 0);
        return (
            <div className='chart-card'>
                <div className='chart-title'>{total} Interactions</div>
                <Pie data={
                    {
                        labels: labels,
                        datasets: [
                            {
                                label: 'interactions',
                                data: metrics,
                                backgroundColor: colors,
                            }
                        ]
                    }
                }
                    options={{
                        responsive: true,
                        plugins: {
                            legend: {
                                labels: { usePointStyle: true, boxHeight: 8 },
                                position: 'bottom',
                            }
                        }
                    }} />
            </div>
        )
    }


    return (
        <>
        {(Array.isArray(metrics) && metrics.length > 0) ? (
            <>
            <Row  className='mb-5'>
                                
                <Col xs={12} sm={8} md={8}>
                <TagLineChart data={metrics}/> 
                </Col>
                <Col xs={12} sm={4} md={4}>
                <TagPieChart data={metrics}/> 
                </Col>
            </Row>
            </>
        ) : (
            null
            )}
            
        </>
    )

}

export default GuardrailSummaryCharts;